import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Edit, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const MACRO_FIELDS = ['calories', 'protein_g', 'carbs_g', 'fats_g'];

export const AdminNutritionPlansTab = ({ api }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [viewWeek, setViewWeek] = useState(1);

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await api().get('/nutrition-plans/admin/all');
      setPlans(res.data.plans || []);
    } catch (e) {
      console.debug('Error loading nutrition plans:', e.message);
    }
    setLoading(false);
  };

  const savePlanEdit = async (planId, weeks, overview) => {
    try {
      await api().put(`/nutrition-plans/admin/${planId}`, { weeks, overview });
      toast.success('Nutrition plan updated');
      fetchPlans();
      setSelectedPlan(null);
    } catch {
      toast.error('Failed to save');
    }
  };

  const updateMeal = (weekIdx, mealIdx, field, value) => {
    if (!selectedPlan) return;
    const updated = { ...selectedPlan, weeks: [...selectedPlan.weeks] };
    const week = { ...updated.weeks[weekIdx], sample_day: [...updated.weeks[weekIdx].sample_day] };
    week.sample_day[mealIdx] = { ...week.sample_day[mealIdx], [field]: value };
    updated.weeks[weekIdx] = week;
    setSelectedPlan(updated);
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="h-8 w-8 text-primary animate-spin mx-auto" /></div>;

  if (selectedPlan) {
    const weekData = selectedPlan.weeks?.[viewWeek - 1];
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <Button variant="ghost" onClick={() => setSelectedPlan(null)} className="text-muted-foreground mb-2">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <h3 className="font-heading text-xl text-white uppercase">{selectedPlan.user?.name}'s Nutrition Plan</h3>
            <p className="text-muted-foreground text-sm">{selectedPlan.program_title} — {selectedPlan.duration_weeks} weeks</p>
          </div>
          <Button
            onClick={() => savePlanEdit(selectedPlan.id, selectedPlan.weeks, selectedPlan.overview)}
            className="bg-green-600 text-white hover:bg-green-700"
          >
            Save Changes
          </Button>
        </div>

        <div className="flex items-center justify-between mb-4 bg-card border border-border p-3">
          <button onClick={() => setViewWeek((w) => Math.max(1, w - 1))} disabled={viewWeek <= 1} className="text-white disabled:text-muted-foreground">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="font-heading text-green-400 uppercase text-sm">Week {viewWeek} — {weekData?.focus || ''}</span>
          <button
            onClick={() => setViewWeek((w) => Math.min(selectedPlan.duration_weeks, w + 1))}
            disabled={viewWeek >= selectedPlan.duration_weeks}
            className="text-white disabled:text-muted-foreground"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-4 gap-2 mb-4">
          {MACRO_FIELDS.map((field) => (
            <div key={field} className="bg-card border border-border p-2 text-center rounded">
              <input
                type="number"
                value={weekData?.daily_targets?.[field] || 0}
                onChange={(e) => {
                  const updated = { ...selectedPlan, weeks: [...selectedPlan.weeks] };
                  const week = { ...updated.weeks[viewWeek - 1] };
                  week.daily_targets = { ...week.daily_targets, [field]: parseInt(e.target.value) || 0 };
                  updated.weeks[viewWeek - 1] = week;
                  setSelectedPlan(updated);
                }}
                className="w-full bg-transparent text-white text-center font-heading text-sm border-none focus:outline-none"
              />
              <p className="text-muted-foreground text-xs">{field.replace('_g', '').replace('calories', 'Cal')}</p>
            </div>
          ))}
        </div>

        <div className="space-y-3">
          {weekData?.sample_day?.map((meal, i) => (
            <div key={i} className="bg-card border border-border p-3 rounded space-y-2">
              <div className="flex gap-2">
                <input
                  value={meal.meal || ''}
                  onChange={(e) => updateMeal(viewWeek - 1, i, 'meal', e.target.value)}
                  className="bg-black text-white font-heading text-sm uppercase px-2 py-1 border border-border rounded w-32"
                />
                <input
                  value={meal.time || ''}
                  onChange={(e) => updateMeal(viewWeek - 1, i, 'time', e.target.value)}
                  className="bg-black text-muted-foreground text-sm px-2 py-1 border border-border rounded w-24"
                />
              </div>
              <textarea
                value={meal.foods || ''}
                onChange={(e) => updateMeal(viewWeek - 1, i, 'foods', e.target.value)}
                className="w-full bg-black text-white text-sm px-2 py-1 border border-border rounded"
                rows={2}
              />
              <div className="grid grid-cols-4 gap-2">
                {MACRO_FIELDS.map((f) => (
                  <input
                    key={f}
                    type="number"
                    value={meal[f] || 0}
                    onChange={(e) => updateMeal(viewWeek - 1, i, f, parseInt(e.target.value) || 0)}
                    className="bg-black text-white text-xs text-center px-1 py-1 border border-border rounded"
                    placeholder={f.replace('_g', '')}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">User Nutrition Plans</h3>

      {plans.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border">
          <p className="text-muted-foreground">No nutrition plans generated yet</p>
          <p className="text-muted-foreground text-sm mt-1">Plans are created when users generate them from their purchased programs</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <div key={plan.id} className="bg-card border border-border p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 font-bold">
                  {plan.user?.name?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{plan.user?.name || 'Unknown'}</p>
                  <p className="text-muted-foreground text-xs">{plan.user?.email}</p>
                </div>
              </div>
              <p className="text-green-400 font-heading text-sm uppercase">{plan.program_title}</p>
              <p className="text-muted-foreground text-xs mb-1">{plan.duration_weeks} weeks</p>
              {plan.overview && (
                <p className="text-muted-foreground text-xs">
                  {plan.overview.daily_calories_start}-{plan.overview.daily_calories_end} cal/day, {plan.overview.meals_per_day} meals
                </p>
              )}
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  className="flex-1 bg-green-600 text-white hover:bg-green-700"
                  onClick={() => { setSelectedPlan(plan); setViewWeek(1); }}
                >
                  <Edit className="h-3 w-3 mr-1" /> View / Edit
                </Button>
              </div>
              {plan.edited_by_admin && <p className="text-xs text-yellow-400 mt-2">Edited by admin</p>}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};
